import { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Calendar as CalIcon, CreditCard, FileText, RefreshCw, Repeat } from "lucide-react";

const TYPE_COLORS = {
    due: { dot: "bg-red-500", text: "text-red-700", bg: "bg-red-50 border-red-200" },
    statement: { dot: "bg-blue-500", text: "text-blue-700", bg: "bg-blue-50 border-blue-200" },
    renewal: { dot: "bg-amber-500", text: "text-amber-700", bg: "bg-amber-50 border-amber-200" },
    expiry: { dot: "bg-stone-700", text: "text-stone-700", bg: "bg-stone-100 border-stone-200" },
    subscription: { dot: "bg-emerald-500", text: "text-emerald-700", bg: "bg-emerald-50 border-emerald-200" },
};

const ICONS = {
    due: CreditCard,
    statement: FileText,
    renewal: RefreshCw,
    expiry: CalIcon,
    subscription: Repeat,
};

export default function CalendarView() {
    const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
    const [events, setEvents] = useState([]);

    useEffect(() => {
        api.get("/calendar/events", { params: { month } }).then((r) => setEvents(r.data));
    }, [month]);

    const [y, m] = month.split("-").map(Number);
    const first = new Date(y, m - 1, 1);
    const daysInMonth = new Date(y, m, 0).getDate();
    const startWeekday = first.getDay(); // 0=Sun

    const byDay = useMemo(() => {
        const map = {};
        events.forEach((e) => {
            const d = e.date?.slice(8, 10);
            if (!d) return;
            map[Number(d)] = map[Number(d)] || [];
            map[Number(d)].push(e);
        });
        return map;
    }, [events]);

    const cells = [];
    for (let i = 0; i < startWeekday; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);

    return (
        <div data-testid="calendar-page">
            <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
                <div>
                    <div className="eyebrow">Calendar</div>
                    <h1 className="font-display text-4xl sm:text-5xl text-stone-900 leading-none mt-2">
                        {first.toLocaleDateString(undefined, { month: "long", year: "numeric" })}
                    </h1>
                    <p className="text-sm text-stone-500 mt-3">{events.length} upcoming events</p>
                </div>
                <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-48" data-testid="cal-month" />
            </div>

            <div className="bg-white border border-stone-200 rounded-lg overflow-hidden">
                <div className="grid grid-cols-7 border-b border-stone-200">
                    {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
                        <div key={d} className="eyebrow px-3 py-2 border-r last:border-r-0 border-stone-200 text-stone-500">{d}</div>
                    ))}
                </div>
                <div className="grid grid-cols-7">
                    {cells.map((d, i) => {
                        const dayEvents = d ? byDay[d] || [] : [];
                        const cellKey = d != null ? `day-${d}` : `empty-${i}`;
                        return (
                            <div key={cellKey} className={`min-h-[110px] border-r last:border-r-0 border-b border-stone-200 p-2 ${d ? "" : "bg-stone-50"}`}>
                                {d && (
                                    <>
                                        <div className="text-xs text-stone-500 tabular-nums mb-1">{d}</div>
                                        <div className="space-y-1">
                                            {dayEvents.slice(0, 3).map((e, idx) => {
                                                const c = TYPE_COLORS[e.type] || TYPE_COLORS.statement;
                                                const Icon = ICONS[e.type] || FileText;
                                                const evKey = `${e.type || "ev"}-${e.id || e.label || idx}`;
                                                return (
                                                    <div key={evKey} className={`text-[10.5px] px-1.5 py-1 rounded border ${c.bg} ${c.text} flex items-center gap-1 truncate`}>
                                                        <Icon size={10} />
                                                        <span className="truncate">{e.label}</span>
                                                    </div>
                                                );
                                            })}
                                            {dayEvents.length > 3 && (
                                                <div className="text-[10px] text-stone-500">+{dayEvents.length - 3} more</div>
                                            )}
                                        </div>
                                    </>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>

            <div className="mt-6 flex flex-wrap gap-4 text-xs text-stone-600">
                {[
                    ["Payment Due", "due"],
                    ["Statement", "statement"],
                    ["Renewal", "renewal"],
                    ["Card Expiry", "expiry"],
                    ["Subscription", "subscription"],
                ].map(([lbl, t]) => (
                    <div key={t} className="flex items-center gap-1.5">
                        <span className={`w-2 h-2 rounded-full ${TYPE_COLORS[t].dot}`} />
                        {lbl}
                    </div>
                ))}
            </div>
        </div>
    );
}
